/* global globalThis */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logoutSession } from "../Services/session";
import {
  logout,
  selectIsAuthenticated,
  updateTokens,
} from "../store/slices/authSlice";

const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000;
const WARNING_BEFORE_LOGOUT_MS = 30 * 1000;
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

const TokenManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const warningVisibleRef = useRef(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(
    WARNING_BEFORE_LOGOUT_MS / 1000
  );

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    await logoutSession();
    clearAllTimers();
    setShowTimeoutWarning(false);
    dispatch(logout());
    navigate("/", { replace: true });
  }, [clearAllTimers, dispatch, navigate]);

  const decrementCountdown = useCallback(() => {
    setCountdownSeconds((previousSeconds) => Math.max(previousSeconds - 1, 0));
  }, []);

  const openTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(true);
    setCountdownSeconds(WARNING_BEFORE_LOGOUT_MS / 1000);

    countdownIntervalRef.current = setInterval(decrementCountdown, 1000);
    logoutTimerRef.current = setTimeout(handleLogout, WARNING_BEFORE_LOGOUT_MS);
  }, [decrementCountdown, handleLogout]);

  const scheduleInactivityWarning = useCallback(() => {
    warningTimerRef.current = setTimeout(
      openTimeoutWarning,
      INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS
    );
  }, [openTimeoutWarning]);

  const startInactivityTracking = useCallback(() => {
    if (!isAuthenticated) return;
    if (warningVisibleRef.current) return;

    clearAllTimers();
    setShowTimeoutWarning(false);
    setCountdownSeconds(WARNING_BEFORE_LOGOUT_MS / 1000);

    scheduleInactivityWarning();
  }, [clearAllTimers, isAuthenticated, scheduleInactivityWarning]);

  const handleStayLoggedIn = useCallback(() => {
    warningVisibleRef.current = false;
    clearAllTimers();
    setShowTimeoutWarning(false);
    setCountdownSeconds(WARNING_BEFORE_LOGOUT_MS / 1000);
    scheduleInactivityWarning();
  }, [clearAllTimers, scheduleInactivityWarning]);

  const handleDialogClose = useCallback((_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
  }, []);

  useEffect(() => {
    const handleTokenExpired = () => {
      handleLogout();
    };

    const handleTokensRefreshed = (event) => {
      const { token, refreshToken } = event.detail;
      dispatch(updateTokens({ token, refreshToken }));
    };

    // Listen for custom events from the HTTP interceptor
    globalThis.addEventListener('tokenExpired', handleTokenExpired);
    globalThis.addEventListener('tokensRefreshed', handleTokensRefreshed);

    return () => {
      globalThis.removeEventListener('tokenExpired', handleTokenExpired);
      globalThis.removeEventListener('tokensRefreshed', handleTokensRefreshed);
    };
  }, [dispatch, handleLogout]);

  useEffect(() => {
    warningVisibleRef.current = showTimeoutWarning;
  }, [showTimeoutWarning]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      setShowTimeoutWarning(false);
      return;
    }

    startInactivityTracking();
    ACTIVITY_EVENTS.forEach((eventName) => {
      globalThis.addEventListener(eventName, startInactivityTracking, {
        passive: true,
      });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        globalThis.removeEventListener(eventName, startInactivityTracking);
      });
      clearAllTimers();
    };
  }, [clearAllTimers, isAuthenticated, startInactivityTracking]);

  return (
    <Dialog
      open={showTimeoutWarning}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth='sm'
      slotProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", sm: "100%", md: "100%", lg: "100%" },
          m: { xs: 1.5, sm: 3, lg: 4, md: 3 },
        },
      }}
      aria-labelledby='session-timeout-dialog-title'
      aria-describedby='session-timeout-dialog-description'
    >
      <DialogTitle
        id='session-timeout-dialog-title'
        sx={{ pb: 1, fontSize: { xs: "1rem", sm: "1.25rem", md: "1.25rem", lg: "1.25rem" } }}
      >
        Session Timeout Warning
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <DialogContentText
          id='session-timeout-dialog-description'
          sx={{ fontSize: { xs: "0.7rem", sm: "1rem", md: "1.1rem", lg: "1.1rem" }, lineHeight: 1.5 }}
        >
          You have been inactive for a while. You will be logged out in {countdownSeconds} seconds.
        </DialogContentText>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          pb: 2,
          pt: 1,
          gap: 1,
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: { xs: "space-between", sm: "flex-end" },
          "& .MuiButton-root": {
            flex: { xs: 1, sm: "0 0 auto" },
            minWidth: { xs: 0, sm: 170 },
          },
        }}
      >
        <Button
          onClick={handleLogout}
          color='error'
          variant='outlined'
          sx={{ minHeight: 44, px: 1, fontSize: { xs: "0.7rem", sm: "1rem" } }}
        >
          Logout Now
        </Button>
        <Button
          onClick={handleStayLoggedIn}
          variant='contained'
          autoFocus
          sx={{ minHeight: 44, px: 1, fontSize: { xs: "0.7rem", sm: "1rem" } }}
        >
          Continue Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenManager;
